/**
 * Route tree — hand-written, because there are four routes and two of them
 * render the same page. The other three apps earn `@tanstack/router-plugin`
 * codegen with dozens of file routes; here it would be a build step guarding
 * nothing.
 *
 * `/` is the address people are given. `/login` exists because that is where
 * the tenant apps send a signed-out visitor (`${BASE_URL}login`), and on this
 * host `BASE_URL` is `/`. `/forgot` asks for a password reset. `/connect` is where
 * the API sends a person to answer an AI app's request to connect. All of them are
 * served the same `index.html`.
 */
import { createRootRoute, createRoute, Outlet } from "@tanstack/react-router";
import type { JSX } from "react";
import { GlobalLoginPage } from "./GlobalLoginPage";
import { ForgotPasswordPage } from "./ForgotPasswordPage";
import { ConnectPage } from "./ConnectPage";

const rootRoute = createRootRoute({
  component: function EntryRoot(): JSX.Element {
    return <Outlet />;
  },
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: GlobalLoginPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: GlobalLoginPage,
});

const forgotRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot",
  component: ForgotPasswordPage,
});

const connectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/connect",
  // Strings or nothing: the page checks the request id itself, and never
  // renders the raw `error` value.
  validateSearch: (search: Record<string, unknown>) => ({
    request: typeof search.request === "string" ? search.request : undefined,
    error: typeof search.error === "string" ? search.error : undefined,
  }),
  component: function ConnectRoute(): JSX.Element {
    const { request, error } = connectRoute.useSearch();
    return <ConnectPage request={request} error={error} />;
  },
});

export const routeTree = rootRoute.addChildren([indexRoute, loginRoute, forgotRoute, connectRoute]);
